import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/location", label: "Die Location" },
  { href: "/preise", label: "Preise & Buchung" },
  { href: "/galerie", label: "Galerie" },
  { href: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? "bg-[#141414]/96 backdrop-blur-md border-b border-[#F2EDE8]/5 shadow-xl"
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
          <svg viewBox="0 0 44 44" fill="none" className="w-9 h-9 flex-shrink-0">
            <text
              x="2"
              y="36"
              fontFamily="'Cormorant Garamond', serif"
              fontSize="34"
              fontWeight="600"
              fill="#D4A843"
            >
              77
            </text>
          </svg>
          <div className="flex flex-col leading-none">
            <span
              className="text-[#F2EDE8] font-semibold tracking-[0.15em] text-sm"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              ATELIER 77
            </span>
            <span
              className="text-[#D4A843]/70 text-xs"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
            >
              Art meets Party
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.href === "/" ? "home" : link.href.slice(1)}`}
              className={`text-[11px] tracking-[0.2em] uppercase transition-colors duration-200 ${
                location.pathname === link.href
                  ? "text-[#C1440E]"
                  : "text-[#F2EDE8]/60 hover:text-[#F2EDE8]"
              }`}
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <a
          href="tel:02349350002"
          className="hidden md:flex items-center gap-2 text-[#D4A843] hover:text-[#C1440E] transition-colors duration-200"
          data-testid="navbar-phone"
        >
          <Phone size={13} />
          <span className="text-xs tracking-wider" style={{ fontFamily: "'Outfit', sans-serif" }}>
            0234 / 935 000 2
          </span>
        </a>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-[#F2EDE8] p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Menü öffnen"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-[#1C1C1E] border-b border-[#F2EDE8]/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.href === "/" ? "home" : link.href.slice(1)}`}
                  className={`text-sm tracking-[0.2em] uppercase py-2 border-b border-[#F2EDE8]/5 ${
                    location.pathname === link.href
                      ? "text-[#C1440E]"
                      : "text-[#F2EDE8]/70"
                  }`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="tel:02349350002"
                className="flex items-center gap-2 text-[#D4A843] mt-3 text-sm"
                data-testid="mobile-phone"
              >
                <Phone size={14} />
                0234 / 935 000 2
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
